import React, { useState, useEffect } from "react";
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import * as inboundQueries from "../../graphql/inbound/custom-queries";
import * as listIdQueries from "../../graphql/list-id-queries";
import * as adminQueries from "../../graphql/admin-queries";
import * as inventoryMutations from "../../graphql/inventory/custom-mutations";
import {
  ALL_RELOAD_YARDS,
  deleteYardData,
  deleteAccount,
  resetYardData,
  resetCarrierAppData,
} from "../yardDelete.js";

import {
  Backdrop,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Grid,
  MenuItem,
  TextField as TextInput,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";

import { ArrayField, Datagrid, TextField, BooleanField } from "react-admin";

import { logError } from "../../utils/appUtils";
import {
  InboundState,
  InboundStatus,
  InventoryStatus,
  LocationType,
  MAX_INVENTORY_QUERY_LIMIT,
  ShipmentLineItemStatus,
} from "../../utils/constants";
import { getLocationFullName, isTrack } from "../../utils/locationUtils";
import { trimDecimal } from "../../utils/utils";
import SwitchOnOff from "../../components/common/AntSwitch";
import { sanitizeInventory } from "../../graphql/inventory/inventoryCRUD.js";

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(2),
    overflow: "auto",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const OrphanCleanupDialogContent = ({
  orphanAudit,
  queryTerms,
  setQueryTerms,
}) => {
  const toDelete = (orphanAudit?.orphans || []).filter(
    (orphan) => orphan.action === "delete",
  );
  const byYard = toDelete.reduce((acc, orphan) => {
    const yard = acc[orphan.reloadYardId] || {
      name: orphan.yardName,
      count: 0,
      capacityTracked: 0,
    };
    yard.count += 1;
    if (orphan.capacityTracked) yard.capacityTracked += 1;
    acc[orphan.reloadYardId] = yard;
    return acc;
  }, {});
  const selected = queryTerms.reloadYardId
    ? byYard[queryTerms.reloadYardId]?.count || 0
    : toDelete.length;

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="body1" gutterBottom>
          Deletes the orphan inventory items found by the audit of{" "}
          {orphanAudit?.finishedAt}: rows whose inventory was deleted with its
          inbound while the rows themselves stayed behind.
        </Typography>
        <Typography variant="body1" gutterBottom>
          Each row is checked again before it goes: its inventory must still be
          missing, nothing may reference it (outbound pick, seal, barcode), and
          it must still name the inventory and yard the audit saw. Rows the
          audit marked for review are never deleted here.
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Location capacity is not changed: the inbound delete already released
          it. For yards with orphans in capacity-tracked locations, run
          auditCapacityUsed afterwards; the console lists them.
        </Typography>
        <Typography variant="body2" color="error" gutterBottom>
          InventoryItem has no point-in-time recovery in prod. Take an on-demand
          backup of the table before running this.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextInput
          select
          id="orphanCleanupYardId"
          label="Reload Yard"
          variant="outlined"
          margin="dense"
          fullWidth
          value={queryTerms.reloadYardId || ""}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, reloadYardId: e.target.value })
          }
          style={{ minWidth: "32rem" }}
          helperText={`${selected} rows will be deleted.`}
        >
          <MenuItem value="">All yards ({toDelete.length})</MenuItem>
          {Object.entries(byYard)
            .sort(([, a], [, b]) => b.count - a.count)
            .map(([id, yard]) => (
              <MenuItem key={id} value={id}>
                {yard.name} - {yard.count}
                {yard.capacityTracked
                  ? ` (${yard.capacityTracked} capacity-tracked)`
                  : ""}
              </MenuItem>
            ))}
        </TextInput>
      </Grid>
    </Grid>
  );
};

const BackfillPlacementDialogContent = ({
  reloadYards,
  queryTerms,
  setQueryTerms,
}) => (
  <Grid container spacing={1}>
    <Grid item xs={12}>
      <Typography variant="body1" gutterBottom>
        Copies the outbound placed time out of <strong>arrivalDateTime</strong>{" "}
        into <strong>placementDateTime</strong>, which is where the dispatch
        tab's "Time Placed" now reads and writes, and moves legacy ARRIVED
        shipments to status <strong>PLACED</strong> so they land in the right
        OutboundList tab.
      </Typography>
      <Typography variant="body2" color="error" gutterBottom>
        Run this once per environment right after the release. Until it runs, an
        already-placed outbound shows an empty "Time Placed", sits in the
        Arrived tab instead of Placed, and saving it rolls the shipment back to
        PENDING.
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Shipments still PENDING are left alone — an arrival time on one of those
        is a driver at the gate, not a placement. ARRIVED shipments carrying a
        gateCheckInId are skipped for the same reason: those arrived through the
        new driver check-in.
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <TextInput
        select
        id="backfillPlacementYardId"
        label="Reload Yard"
        variant="outlined"
        margin="dense"
        fullWidth
        value={queryTerms.reloadYardId || ""}
        onChange={(e) =>
          setQueryTerms({ ...queryTerms, reloadYardId: e.target.value })
        }
        style={{ minWidth: "32rem" }}
        helperText="Leave on All yards to migrate the whole environment at once."
      >
        <MenuItem value="">All yards</MenuItem>
        {reloadYards.map((itm) => (
          <MenuItem key={itm.id} value={itm.id}>
            {itm.name} - {itm.id}
          </MenuItem>
        ))}
      </TextInput>
    </Grid>
    <Grid item xs={12}>
      <SwitchOnOff
        leftLabel="Dry run"
        rightLabel="Apply"
        checked={queryTerms.applyBackfill === true}
        setChecked={(checked) =>
          setQueryTerms({ ...queryTerms, applyBackfill: checked })
        }
      />
    </Grid>
  </Grid>
);

const DeleteBulkErrorLogsDialogContent = ({ queryTerms, setQueryTerms }) => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TextInput
          id="deleteLogPath"
          label="Path (message)"
          variant="outlined"
          margin="dense"
          fullWidth
          value={queryTerms.path || ""}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, path: e.target.value })
          }
          style={{ minWidth: "32rem" }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          id="deleteLogException"
          label="Exception"
          variant="outlined"
          margin="dense"
          fullWidth
          multiline
          rows={3}
          value={queryTerms.exception || ""}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, exception: e.target.value })
          }
          style={{ minWidth: "32rem" }}
        />
      </Grid>
    </Grid>
  );
};

const BackfillTrackCarDialogContent = ({ queryTerms, setQueryTerms }) => {
  const [reloadYards, setReloadYards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pre-populate with current user's reloadYardId if available
  const defaultYardId = window.appUser?.reloadYardId || "";

  useEffect(() => {
    const fetchReloadYards = async () => {
      try {
        setLoading(true);
        const yards = [];
        let nextToken = null;

        do {
          const response = await API.graphql(
            graphqlOperation(queries.listReloadYards, {
              limit: 100,
              nextToken,
            }),
          );
          const page = response?.data?.listReloadYards;
          if (page?.items?.length) {
            yards.push(...page.items);
          }
          nextToken = page?.nextToken || null;
        } while (nextToken);

        // Sort by name for easier selection
        yards.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setReloadYards(yards);

        // Set default value if not already set
        if (!queryTerms.reloadYardId && defaultYardId) {
          setQueryTerms({ ...queryTerms, reloadYardId: defaultYardId });
        }
      } catch (err) {
        console.error("Failed to fetch reload yards:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReloadYards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TextInput
          id="backfillReloadYardId"
          label="Select Reload Yard"
          variant="outlined"
          margin="dense"
          fullWidth
          select
          value={queryTerms.reloadYardId ?? defaultYardId}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, reloadYardId: e.target.value })
          }
          style={{ minWidth: "32rem" }}
          helperText={
            loading
              ? "Loading yards..."
              : "Select the reload yard to backfill TrackCars for"
          }
          disabled={loading}
        >
          {loading ? (
            <MenuItem value="">Loading...</MenuItem>
          ) : (
            reloadYards.map((yard) => (
              <MenuItem key={yard.id} value={yard.id}>
                {yard.name} {yard.id === defaultYardId ? "(current)" : ""}
              </MenuItem>
            ))
          )}
        </TextInput>
      </Grid>
    </Grid>
  );
};

const resourceOptions = [
  { id: "accounts", name: "Account" },
  { id: "announcements", name: "Announcement" },
  { id: "aisles", name: "Aisle" },
  { id: "areas", name: "Area" },
  { id: "audits", name: "Audit" },
  { id: "capabilitys", name: "Capability" },
  { id: "carriers", name: "Carrier" },
  { id: "carrierCompanys", name: "CarrierCompany" },
  { id: "carrierUsers", name: "CarrierUser" },
  { id: "carrierUserReloadYardCarriers", name: "CarrierUserReloadYardCarrier" },
  // CarrierIdentifierClaim is @model(mutations: null) — the claims are the
  // resolver's to maintain, so there is no updateCarrierIdentifierClaim for
  // this to call. handleCarrierChange rewrites them off the Carrier rows above.
  // CarrierUserIdentity is left out on purpose rather than for want of a
  // mutation: it is the drivers' biometric sidecar, and it carries no
  // @searchable index and no composite sort key, so a rewrite of every row
  // would touch that PII for no index to regenerate.
  { id: "chargeRules", name: "chargeRule" },
  { id: "checklists", name: "Checklist" },
  { id: "commoditys", name: "Commodity" },
  { id: "customerReloadYardAccounts", name: "CustomerReloadYardAccount" },
  { id: "eventTypes", name: "EventType" },
  { id: "files", name: "File" },
  { id: "helpPages", name: "HelpPage" },
  { id: "inboundShipments", name: "InboundShipment" },
  { id: "inventories", name: "Inventory" },
  { id: "inventoryItems", name: "InventoryItem" },
  { id: "invoices", name: "Invoice" },
  { id: "invoiceServices", name: "InvoiceService" },
  { id: "locations", name: "Location" },
  { id: "locationTags", name: "LocationTag" },
  // { id: "logs", name: "Log" },
  { id: "outboundShipments", name: "OutboundShipment" },
  { id: "products", name: "Product" },
  { id: "reloadYards", name: "ReloadYard" },
  { id: "reloadYardCapabilitys", name: "ReloadYardCapability" },
  { id: "reloadYardCommoditys", name: "ReloadYardCommodity" },
  { id: "seals", name: "Seal" },
  { id: "services", name: "Service" },
  { id: "setupDatas", name: "SetupData" },
  { id: "shipmentLineItems", name: "ShipmentLineItem" },
  {
    id: "shipmentLineItemInventoryItems",
    name: "ShipmentLineItemInventoryItem",
  },
  // { id: "tags", name: "Tag" },
  { id: "tasks", name: "Task" },
  { id: "timeslots", name: "Timeslot" },
  { id: "trackCars", name: "TrackCar" },
  { id: "users", name: "User" },
  { id: "zones", name: "Zone" },
];

export const elapsedTime = (startTime, endTime, parentCount, prodCount) => {
  const timeDelta = endTime - startTime;
  // Convert milliseconds to minutes and seconds
  const minutes = Math.floor(timeDelta / 60000);
  const seconds = ((timeDelta % 60000) / 1000).toFixed(2);

  const perParentExecutionTime =
    (minutes * 60 + parseFloat(seconds)) / parentCount;
  const estimatedProdExecutionTime = perParentExecutionTime * prodCount;

  console.log(`Execution time: ${minutes} minutes and ${seconds} seconds`);
  console.log(
    `Production execution time: ${Math.floor(
      estimatedProdExecutionTime / 60,
    )} minutes and ${(estimatedProdExecutionTime % 60).toFixed(2)} seconds`,
  );
};

export const getYards = async (setReloadYards, notify) => {
  try {
    debugger;
    const input = { active: "true", limit: 100 };
    let resp = await API.graphql(
      graphqlOperation(queries.reloadYardByName, input),
    );
    let yardData = resp.data.reloadYardByName.items;
    setReloadYards(yardData);
  } catch (error) {
    logError("AdminDashboard:getYards", error, notify);
  }
};

// ################################################################
// ################################################################
// ################################################################
// ################################################################
// ################################################################
// ################################################################

export const saveInventoryItem = (inventoryItem) => {
  debugger;
  const toUpdateInventoryItem = {
    ...inventoryItem,
  };
  delete toUpdateInventoryItem.area;
  delete toUpdateInventoryItem.zone;
  delete toUpdateInventoryItem.aisle;
  delete toUpdateInventoryItem.location;
  delete toUpdateInventoryItem.inventory;
  delete toUpdateInventoryItem.createdAt;
  delete toUpdateInventoryItem.updatedAt;

  // console.log("toUpdateInventoryItem", toUpdateInventoryItem);
  return API.graphql(
    graphqlOperation(mutations.updateInventoryItem, {
      input: toUpdateInventoryItem,
    }),
  );
};

export const saveInventory = (inventory) => {
  const toUpdateRecord = sanitizeInventory(inventory);

  return API.graphql(
    graphqlOperation(inventoryMutations.updateAuditInventorie, {
      input: toUpdateRecord,
    }),
  );
};

export const removeInventory = (inventory) => {
  const input = { id: inventory.id };
  return API.graphql(graphqlOperation(mutations.deleteInventorie, { input }));
};

export const saveProduct = (product) => {
  const toUpdateRecord = { ...product };

  delete toUpdateRecord.account;
  delete toUpdateRecord.createdAt;
  delete toUpdateRecord.updatedAt;

  // console.log("toUpdateProduct", toUpdateRecord)

  return API.graphql(
    graphqlOperation(mutations.updateProduct, { input: toUpdateRecord }),
  );
};

export const saveShipmentLineItem = (shipmentLineItem) => {
  const toUpdateRecord = { ...shipmentLineItem };

  delete toUpdateRecord.inventory;
  delete toUpdateRecord.inventoryItems;
  delete toUpdateRecord.outboundShipment;
  delete toUpdateRecord.createdAt;
  delete toUpdateRecord.updatedAt;
  return API.graphql(
    graphqlOperation(mutations.updateShipmentLineItem, {
      input: toUpdateRecord,
    }),
  );
};

export const removeShipmentLineItem = (shipmentLineItem) => {
  const input = { id: shipmentLineItem.id };
  return API.graphql(
    graphqlOperation(mutations.deleteShipmentLineItem, { input }),
  );
};

export const saveShipmentLineItemInventoryItem = (
  ShipmentLineItemInventoryItem,
) => {
  const toUpdateRecord = { ...ShipmentLineItemInventoryItem };

  delete toUpdateRecord.inventoryItems;
  delete toUpdateRecord.createdAt;
  delete toUpdateRecord.updatedAt;
  return API.graphql(
    graphqlOperation(mutations.updateShipmentLineItemInventoryItem, {
      input: toUpdateRecord,
    }),
  );
};

export const saveLocation = (location) => {
  const toUpdateRecord = { ...location };

  delete toUpdateRecord.area;
  delete toUpdateRecord.zone;
  delete toUpdateRecord.aisle;
  delete toUpdateRecord.tags;
  delete toUpdateRecord.createdAt;
  delete toUpdateRecord.updatedAt;
  return API.graphql(
    graphqlOperation(mutations.updateLocation, { input: toUpdateRecord }),
  );
};

export const saveAisle = (aisle) => {
  const toUpdateRecord = { ...aisle };

  delete toUpdateRecord.area;
  delete toUpdateRecord.zone;
  delete toUpdateRecord.aisle;
  delete toUpdateRecord.tags;
  delete toUpdateRecord.createdAt;
  delete toUpdateRecord.updatedAt;
  return API.graphql(
    graphqlOperation(mutations.updateAisle, { input: toUpdateRecord }),
  );
};

// ################################################################
// ################################################################
// ################################################################
// ################################################################
// ################################################################
// ################################################################

const updateResource = (dataProvider, resource, record) => {
  const recordToUpdate = { ...record };
  Object.entries(recordToUpdate).forEach(([key, value]) => {
    if (
      !key.endsWith("DateTime") &&
      !key.endsWith("Id") &&
      (typeof value === "object" || Array.isArray(value))
    ) {
      delete recordToUpdate[key];
    }
  });

  return dataProvider.update(resource, { data: recordToUpdate });
};

export const runDBIndexRegen = async (queryTerms, notify, dataProvider) => {
  console.clear();
  let totalRecords = 0;
  let rejectedCount = 0;
  debugger;

  try {
    let hasMoredData = true;
    let page = 1;
    let perPage = MAX_INVENTORY_QUERY_LIMIT;
    while (hasMoredData) {
      const response = await dataProvider.getList(queryTerms?.resource, {
        filter: {},
        pagination: { page, perPage },
        sort: { field: "id", order: "ASC" },
      });
      const data = response.data;
      console.log("data:", data);
      totalRecords += data.length;
      debugger;

      let promiseList = [];
      data.forEach((record) => {
        promiseList.push(
          updateResource(dataProvider, queryTerms.resource, record),
        );
      });

      const results = await Promise.allSettled(promiseList);
      for (const resp of results) {
        if (resp.status === "rejected") {
          rejectedCount++;
          console.log("REJECTED: ", resp);
        }
      }
      const total = page * perPage;
      if (response.total > total) {
        page++;
      } else {
        hasMoredData = false;
      }
      debugger;
    }

    console.log("done");
    notify("All done");
  } catch (error) {
    logError("AdminDashboard:runDBIndexRegen", error, notify);
  } finally {
    console.log("totalRecords: ", totalRecords);
    if (rejectedCount) console.log("rejectedCount", rejectedCount);
  }
};

// export const inventoryQuantityAudit = async (queryTerms, notify) => {
// 	console.clear();
// 	console.log(queryTerms.reloadYardId);
// 	debugger;
// 	// check if inventory qty onHand & committed is correct based on shipmentLineItems shipped count.

// 	try {
// 		const searchInput = {
// 			sort: {field: "createdAt", direction: "desc"},
// 			filter: {
// 				reloadYardId: {eq: queryTerms.reloadYardId},
// 				// status: {ne: InventoryStatus.IN_TRANSIT},
// 			},
// 			limit: 100
// 		}
// 		let nextToken = true;
// 		while (nextToken) {
// 			const input = nextToken === true ? searchInput : {nextToken, ...searchInput};
// 			let result = await API.graphql(graphqlOperation(adminQueries.searchInventorysBackfill, input));
// 			const inventoryList = result.data.searchInventorys.items;
// 			nextToken = result.data.searchInventorys.nextToken;

// 			inventoryList.forEach(inventory => {
// 				let remainingOnHand = (inventory.quantityReceived || 0); // quantityReceived - qty shipped oubtound
// 				let totalInvItemOnHand = 0;
// 				let totalInvItemCommitted = 0;
// 				let totalCommitted = 0;
// 				let totalInventoryItems = 0

// 				// if (inventory.shipmentLineItems?.items?.length > 0) {
// 				// 	for (const lineItem of inventory.shipmentLineItems.items) {
// 				// 		const qtyToPick = lineItem.totalQuantityToPick;
// 				// 		if (lineItem.status === ShipmentLineItemStatus.SHIPPED) {
// 				// 			remainingOnHand -= qtyToPick;
// 				// 		} else {
// 				// 			totalCommitted += qtyToPick;
// 				// 		}
// 				// 	}
// 				// }

// 				// if (inventory.inventoryItems?.items?.length > 0) {
// 				// 	totalInventoryItems = inventory.inventoryItems?.items?.length;

// 				// 	for (const invItem of inventory.inventoryItems.items) {
// 				// 		totalInvItemOnHand += invItem.quantityOnHand;
// 				// 		totalInvItemCommitted += invItem.quantityCommitted;
// 				// 	}
// 				// }

// 				// if (inventory.status === InventoryStatus.UNLOADED && inventory.quantityReceived !== totalInvItemOnHand) {
// 				// 	console.log("UNLOADED Quantity OnHand mismatch:", inventory, inventory.id, inventory.quantityReceived, totalInvItemOnHand);
// 				// }
// 				// if (inventory.status === InventoryStatus.STORED && (remainingOnHand !== inventory.quantityOnHand || inventory.quantityOnHand !== totalInvItemOnHand)) {
// 				// 	console.log("Quantity OnHand mismatch:", inventory, inventory.id, inventory.quantityOnHand, remainingOnHand, totalInvItemOnHand);
// 				// }
// 				// if ((inventory.status === InventoryStatus.SHIPPED && inventory.quantityOnHand > 0) ||
// 				// 	(inventory.quantityOnHand === 0 && inventory.locationId && inventory.status !== InventoryStatus.SHIPPED)
// 				// ) {
// 				// 	console.log("status mismatch:", inventory, inventory.id, inventory.quantityOnHand, inventory.status);
// 				// }
// 				const totalDelivered = inventory.quantityAdvised + (inventory.overShipmentQuantity || 0) - (inventory.underShipmentQuantity || 0);
// 				if (inventory.status !== InventoryStatus.IN_TRANSIT && inventory.status !== InventoryStatus.ARRIVED &&
// 					(inventory.quantityReceived || 0) !== totalDelivered
// 				) {
// 					console.log("Over/Under mismatch:", inventory, inventory.id, inventory.quantityReceived, remainingOnHand, inventory.overShipmentQuantity, inventory.underShipmentQuantity)
// 				}
// 				if (totalCommitted !== inventory.quantityCommitted || totalCommitted !== totalInvItemCommitted) {
// 					console.log("Quantity Committed mismatch:", inventory, inventory.id, inventory.quantityCommitted, totalCommitted, totalInvItemCommitted);
// 				}
// 				// if (inventory.status !== InventoryStatus.IN_TRANSIT && inventory.status !== InventoryStatus.ARRIVED && totalInventoryItems === 0) {
// 				// 	console.log("missing inventoryItem:", inventory, inventory.id)
// 				// }
// 			});
// 		}
// 	} catch (err) {
// 		logError("AdminDashboard:checkInvQuantity", err, notify);
// 	}
// 	notify("All done");
// }

export const auditInbounds = async (queryTerms, notify) => {
  console.clear();
  console.log(queryTerms.reloadYardId);
  debugger;

  try {
    let nextToken = true;
    let searchInput = {
      reloadYardId: queryTerms.reloadYardId,
      limit: 500,
    };
    const inboundMap = {};

    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { nextToken, ...searchInput };
      let result = await API.graphql(
        graphqlOperation(
          inboundQueries.inboundShipmentByReloadYardByAccount,
          input,
        ),
      );
      const inboundList =
        result.data.inboundShipmentByReloadYardByAccount.items;
      console.log("inboundList", inboundList);
      nextToken = result.data.inboundShipmentByReloadYardByAccount.nextToken;
      let count = 0;

      for (const inbound of inboundList) {
        inboundMap[inbound.id] = inbound;
        // if (outbound.id === "05bd3ef9-6363-48fa-bd60-8b940147b906") debugger;
        if (
          (!inbound.inventories || inbound.inventories?.items?.length === 0) &&
          inbound.status === "RELEASED"
        ) {
          console.log("empty inbound", inbound.id);
          count++;
        }
      }

      console.log(
        "empty inbounds",
        count,
        "good inbounds",
        inboundList.length - count,
      );

      // #####################################################
      nextToken = true;
      let zombieInventoryCount = 0;
      let totalInventories = 0;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        let result = await API.graphql(
          graphqlOperation(adminQueries.inventoryByStatusByLot, input),
        );
        const inventoryList = result.data.inventoryByStatusByLot.items;
        nextToken = result.data.inventoryByStatusByLot.nextToken;
        console.log("inventoryList", inventoryList);
        totalInventories += inventoryList.length;

        for (const inventory of inventoryList) {
          if (!inboundMap[inventory.inboundShipmentId]) {
            console.log("zombie inventory", inventory, inventory.id);
            zombieInventoryCount++;
          }
        }
      }
      console.log(
        "zombie inventories",
        zombieInventoryCount,
        "good inventories",
        totalInventories - zombieInventoryCount,
      );
    }
  } catch (err) {
    logError("AdminDashboard:runInboundsAudit", err, notify);
  }
  notify("All done");
};

export const auditOutbounds = async (queryTerms, notify) => {
  console.clear();
  console.log(queryTerms.reloadYardId);
  debugger;

  try {
    let nextToken = true;
    let searchInput = {
      reloadYardId: queryTerms.reloadYardId,
      limit: 500,
    };
    const outboundMap = {};
    let emptyOutboundCount = 0;
    let totalOutbounds = 0;
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { nextToken, ...searchInput };
      let result = await API.graphql(
        graphqlOperation(adminQueries.outboundShipmentByStatusDeleteMe, input),
      );
      const outboundList = result.data.outboundShipmentByStatus.items;
      console.log("outboundList", outboundList);
      nextToken = result.data.outboundShipmentByStatus.nextToken;
      totalOutbounds += outboundList.length;

      for (const outbound of outboundList) {
        outboundMap[outbound.id] = outbound;
        if (
          (!outbound.shipmentLineItems ||
            outbound.shipmentLineItems?.items?.length === 0) &&
          outbound.status === "RELEASED"
        ) {
          console.log(
            "empty outbound",
            outbound.id,
            outbound.createdAt,
            outbound.status,
          );
          emptyOutboundCount++;
        }
      }
    }
    console.log(
      "empty outbounds",
      emptyOutboundCount,
      "good outbounds",
      totalOutbounds - emptyOutboundCount,
    );

    nextToken = true;
    let inventoryMap = {};
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { nextToken, ...searchInput };
      let result = await API.graphql(
        graphqlOperation(adminQueries.inventoryByStatusByLot, input),
      );
      const inventoryList = result.data.inventoryByStatusByLot.items;
      nextToken = result.data.inventoryByStatusByLot.nextToken;
      console.log("inventoryList", inventoryList);
      inventoryList.forEach((itm) => (inventoryMap[itm.id] = itm));
    }

    searchInput = { limit: 500 };
    nextToken = true;
    let zombieLineItemCount = 0;
    let totalLineItems = 0;
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { nextToken, ...searchInput };
      let result = await API.graphql(
        graphqlOperation(listIdQueries.listShipmentLineItemIds, input),
      );
      const lineItemList = result.data.listShipmentLineItems.items.filter(
        (itm) => itm.reloadYardId === queryTerms.reloadYardId,
      );
      nextToken = result.data.listShipmentLineItems.nextToken;
      console.log("lineItemList", lineItemList);
      totalLineItems += lineItemList.length;

      for (const lineItem of lineItemList) {
        if (!outboundMap[lineItem.outboundShipmentId]) {
          console.log(
            "zombie line item",
            lineItem.id,
            inventoryMap[lineItem.inventoryId],
          );
          zombieLineItemCount++;
        }
      }
    }
    console.log(
      "zombie line items",
      zombieLineItemCount,
      "good line items",
      totalLineItems - zombieLineItemCount,
    );
  } catch (err) {
    logError("AdminDashboard:runOutboundsAudit", err, notify);
  }
  notify("All done");
};

export const auditLocations = async (queryTerms, notify) => {
  console.clear();
  console.log(queryTerms.reloadYardId);
  // check location integrity to see if locations have corresponding area, zone, aisle

  try {
    let results = await API.graphql(
      graphqlOperation(queries.listAreas, { limit: 1000 }),
    );
    const areas = results.data.listAreas.items.filter(
      (itm) => itm.reloadYardId === queryTerms.reloadYardId,
    );
    console.log("areas:", areas);
    const areaMap = areas.reduce((acc, obj) => {
      acc[obj.id] = obj;
      return acc;
    }, {});

    results = await API.graphql(
      graphqlOperation(queries.listZones, { limit: 1000 }),
    );
    const zones = results.data.listZones.items.filter(
      (itm) => itm.reloadYardId === queryTerms.reloadYardId,
    );
    console.log("zones:", zones);
    const zoneMap = zones.reduce((acc, obj) => {
      acc[obj.id] = obj;
      return acc;
    }, {});

    results = await API.graphql(
      graphqlOperation(listIdQueries.listAisleIds, { limit: 1000 }),
    );
    const aisles = results.data.listAisles.items.filter(
      (itm) => itm.reloadYardId === queryTerms.reloadYardId,
    );
    console.log("aisles:", aisles);
    const aisleMap = aisles.reduce((acc, obj) => {
      acc[obj.id] = obj;
      return acc;
    }, {});

    results = await API.graphql(
      graphqlOperation(listIdQueries.listLocationIds, { limit: 1000 }),
    );
    let locations = results.data.listLocations.items.filter(
      (itm) => itm.reloadYardId === queryTerms.reloadYardId,
    );
    console.log("Locations:", locations);

    for (const zone of zones) {
      let area = areaMap[zone.areaId];

      if (!area) {
        console.log("zone missing area:", zone, zone.id, "areaId", zone.areaId);
        delete zoneMap[zone.id];
      }
    }

    for (const aisle of aisles) {
      let area = areaMap[aisle.areaId];
      let zone = zoneMap[aisle.zoneId];

      if (!area) {
        console.log(
          "aisle missing area:",
          aisle,
          aisle.id,
          "areaId",
          aisle.areaId,
        );
        delete aisleMap[aisle.id];
      }
      if (!zone) {
        console.log(
          "aisle missing zone:",
          aisle,
          aisle.id,
          "zoneId",
          aisle.zoneId,
        );
        delete aisleMap[aisle.id];
      }
    }

    for (const location of locations) {
      let area = areaMap[location.areaId];
      let zone = zoneMap[location.zoneId];
      let aisle = aisleMap[location.aisleId];

      if (!area) {
        console.log(
          "location missing area:",
          location,
          location.id,
          "areaId",
          location.areaId,
        );
      }
      if (!zone) {
        console.log(
          "location missing zone:",
          location,
          location.id,
          "zoneId",
          location.zoneId,
        );
      }
      if (!aisle) {
        console.log(
          "location missing aizle:",
          location,
          location.id,
          "aisleId",
          location.aisleId,
        );
      }
    }

    console.log("done");
    notify("All done");
  } catch (error) {
    logError("AdminDashboard:runLocationsAudit", error, notify);
  }
};

export const auditInvItemLocations = async (queryTerms, notify) => {
  console.clear();
  console.log(queryTerms.reloadYardId);
  // check location integrity to see if locations have corresponding area, zone, aisle

  try {
    let locationList = [];
    const areaMap = {};
    const zoneMap = {};
    const aisleMap = {};
    const locationMap = {};

    let searchInput = {
      reloadYardId: queryTerms.reloadYardId,
      limit: 1000,
    };
    let nextToken = true;
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { nextToken, ...searchInput };
      let result = await API.graphql(
        graphqlOperation(adminQueries.locationBySortKey, input),
      );
      locationList = locationList.concat(result.data.locationBySortKey.items);
      nextToken = result.data.locationBySortKey.nextToken;
    }
    console.log("locationList", locationList);

    locationList.forEach((location) => {
      const areaId = location.areaId;
      const area = location.area;
      if (!areaMap[areaId]) areaMap[areaId] = [];
      areaMap[areaId].push(area);

      const zoneId = location.zoneId;
      const zone = location.zone;
      if (!zoneMap[zoneId]) zoneMap[zoneId] = [];
      zoneMap[zoneId].push(zone);

      const aisleId = location.aisleId;
      const aisle = location.aisle;
      if (!aisleMap[aisleId]) aisleMap[aisleId] = [];
      aisleMap[aisleId].push(aisle);

      const locationId = location.id;
      locationMap[locationId] = location;
    });

    searchInput = {
      reloadYardId: queryTerms.reloadYardId,
      statusLotNumber: {
        beginsWith: { status: InventoryStatus.STORED },
      },
      limit: 500,
    };
    nextToken = true;
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { nextToken, ...searchInput };
      let result = await API.graphql(
        graphqlOperation(adminQueries.inventoryByStatusByLot, input),
      );
      const inventoryList = result.data.inventoryByStatusByLot.items.filter(
        (itm) => {
          // keep if has aisleId and locationId for location check
          return itm.locationId && itm.aisleId;
        },
      );
      nextToken = result.data.inventoryByStatusByLot.nextToken;
      console.log("inventoryList", inventoryList);

      inventoryList.forEach((inventory) => {
        let area = areaMap[inventory.areaId];
        let zone = zoneMap[inventory.zoneId];
        let aisle = aisleMap[inventory.aisleId];
        let location = locationMap[inventory.locationId];

        if (!area) {
          debugger;
          console.log(
            "inventory missing area:",
            inventory,
            inventory.id,
            "areaId",
            inventory.areaId,
          );
        }
        if (!zone) {
          console.log(
            "inventory missing zone:",
            inventory,
            inventory.id,
            "zoneId",
            inventory.zoneId,
          );
        }
        if (!aisle) {
          console.log(
            "inventory missing aizle:",
            inventory,
            inventory.id,
            "aisleId",
            inventory.aisleId,
          );
        }
        if (!location) {
          console.log(
            "inventory missing location:",
            inventory,
            inventory.id,
            "locationId",
            inventory.locationId,
          );
        }

        // console.log("inventoryItemList", inventoryItemList);
        // const inventoryItemMap = {};
        // inventoryItemList.forEach(item => {
        // 	const locationId = item.locationId;
        // 	if (locationId) {
        // 		if (!inventoryItemMap[locationId]) {
        // 			inventoryItemMap[locationId] = [];
        // 		}
        // 		inventoryItemMap[locationId].push(item);
        // 	}
        // });
      });
    }

    console.log("done");
    notify("All done");
  } catch (error) {
    logError("AdminDashboard:runInvLocationsAudit", error, notify);
  }
};

// ################################################################
// ################################################################
// ################################################################
// ################################################################
// ################################################################
// ################################################################

export const updateAccuntRecords = async (
  dataProvider,
  resource,
  records,
  queryTerms,
) => {
  let promiseList = [];
  console.log("resource, records", resource, records);
  debugger;

  records.forEach((record) => {
    const newRecord = { ...record };
    if (queryTerms.changeId) {
      // change accountId and accountName if needed.
      if (resource === "inboundShipments" && !newRecord.scheduledDateTime) {
        newRecord.scheduledDateTime = null;
      } else if (resource === "outboundShipments") {
        if (!newRecord.scheduledDateTime) {
          newRecord.scheduledDateTime = null;
        }
        if (newRecord.accountId === newRecord.billToAccountId) {
          newRecord.billToAccountId = queryTerms.toAccount.id;
        }
      }

      // applies to all resourves
      newRecord.accountId = queryTerms.toAccount.id;

      if (newRecord.accountName)
        newRecord.accountName = queryTerms.toAccount.businessName;
      else delete newRecord.accountName; // applies to shipmentLineItems

      // applies to inventory records only
      if (newRecord.productId) newRecord.productId = null;
      if (newRecord.productCode) newRecord.productCode = null;
    } else {
      // reset account Name only - do not change accountId
      if (newRecord.accountName)
        newRecord.accountName = queryTerms.fromAccount.businessName;
      else delete newRecord.accountName; // applies to shipmentLineItems
    }

    promiseList.push(updateResource(dataProvider, resource, newRecord));
  });

  const results = await Promise.allSettled(promiseList);
  results.forEach((resp) => {
    if (resp.status === "rejected") console.log("REJECTED: ", resp);
  });
};

export const runAccountChange = async (queryTerms, notify, dataProvider) => {
  console.clear();
  // change accountId and name for all records (inbound, inventory, outbound, lineItems, invoices)
  debugger;

  try {
    let searchInput = {
      filter: {
        reloadYardId: { eq: queryTerms.reloadYardId },
        accountId: { eq: queryTerms.fromAccount.id },
      },
      limit: 1000,
    };
    if (queryTerms.searchTerm) {
      const value = queryTerms.searchTerm;
      searchInput.filter.or = [
        { id: { eq: `${value}` } },
        { poNumber: { matchPhrasePrefix: `${value}` } },
        { poNumber: { wildcard: `*${value}*` } },
        { railCarNumber: { matchPhrasePrefix: `${value}` } },
        { railCarNumber: { wildcard: `*${value}*` } },
        { lotNumber: { matchPhrasePrefix: `${value}` } },
        { lotNumber: { wildcard: `*${value}*` } },
        { customFields: { matchPhrasePrefix: `${value}` } },
        { customFields: { wildcard: `*${value}*` } },
      ];
    }
    let response = await API.graphql(
      graphqlOperation(adminQueries.searchInboundShipments, searchInput),
    );
    const inbounds = response.data.searchInboundShipments.items;
    console.log("inbounds:", inbounds, inbounds.length);
    await updateAccuntRecords(
      dataProvider,
      "inboundShipments",
      inbounds,
      queryTerms,
    );

    // const invoices = inbounds.filter(({ invoice }) => invoice).map(({ invoice }) => invoice);
    // console.log("invoices:", invoices, invoices.length);
    // await updateAccuntRecords(dataProvider, "invoices", invoices, queryTerms);

    const inventories = [].concat(
      ...inbounds.map(({ inventories: { items } }) => items),
    );
    console.log("inventories:", inventories, inventories.length);
    await updateAccuntRecords(
      dataProvider,
      "inventories",
      inventories,
      queryTerms,
    );

    const inventoryItems = [].concat(
      ...inventories
        .filter(({ inventoryItems }) => inventoryItems && inventoryItems.items)
        .map(({ inventoryItems: { items } }) => items),
    );
    console.log("inventoryItems:", inventoryItems, inventoryItems.length);
    await updateAccuntRecords(
      dataProvider,
      "inventoryItems",
      inventoryItems,
      queryTerms,
    );

    const shipmentLineItems = [].concat(
      ...inventories
        .filter(
          ({ shipmentLineItems }) =>
            shipmentLineItems && shipmentLineItems.items,
        )
        .map(({ shipmentLineItems: { items } }) => items),
    );
    console.log(
      "shipmentLineItems:",
      shipmentLineItems,
      shipmentLineItems.length,
    );
    await updateAccuntRecords(
      dataProvider,
      "shipmentLineItems",
      shipmentLineItems,
      queryTerms,
    );
    const outboundMap = {};
    shipmentLineItems.forEach(
      (itm) => (outboundMap[itm.outboundShipmentId] = true),
    );
    // shipmentLineItemsInberntoryItem does not have accountId - no need to process.

    debugger;
    if (queryTerms.searchTerm) {
      const value = queryTerms.searchTerm;
      searchInput.filter.or = [
        { poNumber: { matchPhrasePrefix: `${value}` } },
        { poNumber: { wildcard: `*${value}*` } },
        { railCarNumber: { matchPhrasePrefix: `${value}` } },
        { railCarNumber: { wildcard: `*${value}*` } },
        { customFields: { matchPhrasePrefix: `${value}` } },
        { customFields: { wildcard: `*${value}*` } },
      ];
    }
    response = await API.graphql(
      graphqlOperation(adminQueries.searchOutboundShipments, searchInput),
    );
    let outbounds = response.data.searchOutboundShipments.items;
    outbounds = outbounds.filter((itm) => outboundMap[itm.id]);
    console.log("outbounds:", outbounds, outbounds.length);
    await updateAccuntRecords(
      dataProvider,
      "outboundShipments",
      outbounds,
      queryTerms,
    );

    // const outInvoices = outbounds.filter(({ invoice }) => invoice).map(({ invoice }) => invoice);
    // console.log("outInvoices:", outInvoices, outInvoices.length);
    // await updateAccuntRecords(dataProvider, "invoices", outInvoices, queryTerms);

    console.log("done");
    notify("All done");
  } catch (error) {
    logError("AdminDashboard:runAccountChange", error, notify);
  }
};

export const auditCapacityUsed = async (queryTerms, notify, dataProvider) => {
  await fixCapacityUsed(queryTerms, notify, dataProvider, true);
};

export const fixCapacityUsed = async (
  queryTerms,
  notify,
  dataProvider,
  reportOnly,
) => {
  console.clear();
  console.log(queryTerms.reloadYardId);
  debugger;

  try {
    const promiseList = [];
    let locationList = [];
    const aisleMap = {};
    let searchInput = {
      reloadYardId: queryTerms.reloadYardId,
      limit: 1000,
    };
    let nextToken = true;
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { nextToken, ...searchInput };
      let result = await API.graphql(
        graphqlOperation(adminQueries.locationBySortKey, input),
      );
      // handle bulk in the future by bulk area
      const structuredOnly = result.data.locationBySortKey.items.filter(
        (location) =>
          location.active === "true" &&
          location.type === LocationType.STRUCTURED &&
          !location.locationLoad.other.selected,
      );
      locationList = locationList.concat(structuredOnly);
      nextToken = result.data.locationBySortKey.nextToken;
    }
    console.log("locationList", locationList);

    // create aisleMap to query by aisle
    locationList.forEach((location) => {
      const aisleId = location.aisleId;
      if (!aisleMap[aisleId]) {
        aisleMap[aisleId] = [];
      }
      aisleMap[aisleId].push(location);
    });

    let totalErrors = 0;
    for (const aisleId of Object.keys(aisleMap)) {
      // query inventoryItems by aisle
      let inventoryItemList = [];
      // query assigned / allocated inventoryItems
      searchInput = {
        aisleId: aisleId,
        status: { beginsWith: InventoryStatus.IN_TRANSIT },
        limit: 1000,
      };
      nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(
            adminQueries.inventoryItemByAisleByStatusAudit,
            input,
          ),
        );
        inventoryItemList = inventoryItemList.concat(
          result.data.inventoryItemByAisleByStatus.items,
        );
        nextToken = result.data.inventoryItemByAisleByStatus.nextToken;
      }
      // query stored inventoryItems
      searchInput = {
        aisleId: aisleId,
        status: { beginsWith: InventoryStatus.UNLOADED },
        limit: 1000,
      };
      nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(
            adminQueries.inventoryItemByAisleByStatusAudit,
            input,
          ),
        );
        inventoryItemList = inventoryItemList.concat(
          result.data.inventoryItemByAisleByStatus.items,
        );
        nextToken = result.data.inventoryItemByAisleByStatus.nextToken;
      }
      searchInput = {
        aisleId: aisleId,
        status: { beginsWith: InventoryStatus.STORED },
        limit: 1000,
      };
      nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true ? searchInput : { nextToken, ...searchInput };
        const result = await API.graphql(
          graphqlOperation(
            adminQueries.inventoryItemByAisleByStatusAudit,
            input,
          ),
        );
        inventoryItemList = inventoryItemList.concat(
          result.data.inventoryItemByAisleByStatus.items,
        );
        nextToken = result.data.inventoryItemByAisleByStatus.nextToken;
      }
      // location.area.type === "BULK"
      // handle bulk here in the future.
      // location.qtyCount += invItem.quantityOnHand;
      // console.log("inventoryItemList", inventoryItemList);

      // inventory Items by location ID
      const inventoryItemMap = {};
      inventoryItemList.forEach((item) => {
        const locationId = item.locationId;
        if (locationId) {
          if (!inventoryItemMap[locationId]) {
            inventoryItemMap[locationId] = [];
          }
          inventoryItemMap[locationId].push(item);
        }
      });
      // console.log("inventoryItemMap", inventoryItemMap);

      const locations = aisleMap[aisleId];
      for (const location of locations) {
        location.allocatedCount = 0;
        location.allocatedUnits = 0;
        location.usedCount = 0;
        location.usedUnits = 0;
        const localInventoryItems = inventoryItemMap[location.id] || [];
        // console.log("localInventoryItems", localInventoryItems);

        for (const inventoryItem of localInventoryItems) {
          const inventoryLoadType =
            inventoryItem.inventory.reloadYardCommodity.commodity
              .locationLoadType;
          if (!inventoryLoadType) {
            console.log(
              "inventoryLoadType missing (inv, invItm)",
              inventoryItem.inventoryId,
              inventoryItem.id,
            );
          } else if (inventoryLoadType.includes("Bulk")) {
            continue;
          } else {
            // assuming 1 inventoryItem = 1 capacity load regardless of units
            const load = location.locationLoad[inventoryLoadType];
            if (inventoryItem.status === InventoryStatus.IN_TRANSIT) {
              location.allocatedCount += load.capacity;
              location.allocatedUnits++;
            } else {
              // UNLOADED || STORED
              location.usedCount += load.capacity;
              location.usedUnits++;
            }
            location.itemProductId = inventoryItem.inventory.productId;
          }
        }

        let toUpdateLocation = { ...location };
        let hasErrors = false;

        if ((location.capacityAllocated || 0) !== location.allocatedCount) {
          console.log(
            "Allocated",
            location.id,
            getLocationFullName(location),
            "allocated",
            location.capacityAllocated,
            "calc",
            location.allocatedCount,
            "items",
            location.allocatedUnits,
          );
          toUpdateLocation.capacityAllocated = location.allocatedCount;
          hasErrors = true;
          totalErrors++;
        }

        if ((location.capacityUsed || 0) !== location.usedCount) {
          console.log(
            "     Used",
            location.id,
            getLocationFullName(location),
            "used",
            location.capacityUsed,
            "calc",
            location.usedCount,
            "items",
            location.usedUnits,
          );
          toUpdateLocation.capacityUsed = location.usedCount;
          hasErrors = true;
          totalErrors++;
        }

        if (
          location.productId &&
          location.productId !== location.itemProductId
        ) {
          console.log(
            "  product",
            location.id,
            getLocationFullName(location),
            "loc product",
            location.productId,
            "inv product",
            location.itemProductId,
          );
          toUpdateLocation.productId = location.itemProductId || "";
          hasErrors = true;
          totalErrors++;
        }

        if (hasErrors && !reportOnly) {
          delete toUpdateLocation.area;
          delete toUpdateLocation.zone;
          delete toUpdateLocation.aisle;
          delete toUpdateLocation.locationLoad;

          delete toUpdateLocation.itemProductId;
          delete toUpdateLocation.allocatedCount;
          delete toUpdateLocation.allocatedUnits;
          delete toUpdateLocation.usedCount;
          delete toUpdateLocation.usedUnits;

          console.log("toUpdateLocation", toUpdateLocation);
          promiseList.push(
            API.graphql(
              graphqlOperation(mutations.updateLocation, {
                input: toUpdateLocation,
              }),
            ),
          );
        }
      }

      let results = await Promise.allSettled(promiseList);
      results.forEach((resp) => {
        if (resp.status === "rejected") console.log("rejected", resp.reason);
      });
      debugger;
    }

    console.log("Total Errors", totalErrors);
    console.log("done");
    notify("All done");
  } catch (error) {
    logError("AdminDashboard:fixCapacityUsed", error, notify);
  }
};

export const auditInventoryQuantity = async (
  queryTerms,
  notify,
  dataProvider,
) => {
  await fixInventoryQty(queryTerms, notify, dataProvider, true);
};

export const fixInventoryQty = async (
  queryTerms,
  notify,
  dataProvider,
  reportOnly,
) => {
  console.clear();
  // fix Qty OnHand, Qty Committed, Status, Units based on shipmentLineItems shipped count.
  // --- ignore other exception inventories
  // --- ignore bulk zeroed out ???

  debugger;
  try {
    // TODO
    // add logic if reloadYardId is NOT passed - run all inventories
    // const searchInput =  { limit: 250 };
    // let nextToken = true;
    // while (nextToken) {
    // 	const input = nextToken === true ? searchInput : {nextToken, ...searchInput};
    // 	let result = await API.graphql(graphqlOperation(adminQueries.listInventoriesAudit, input));
    // 	const inventoryList = result.data.listInventories.items;
    // 	nextToken = result.data.listInventories.nextToken;

    const searchInput = {
      filter: {
        reloadYardId: { eq: queryTerms.reloadYardId },
        otherException: { ne: true },
      },
      limit: 250,
    };
    let nextToken = true;
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { nextToken, ...searchInput };
      const result = await API.graphql(
        graphqlOperation(adminQueries.searchInventorysBackfill, input),
      );
      nextToken = result.data.searchInventorys.nextToken;
      const inventoryList = result.data.searchInventorys.items;
      console.log("inventoryList", inventoryList);

      const promiseList = [];
      for (const inventory of inventoryList) {
        let totalReceived = inventory.quantityReceived || 0;
        let remainingOnHand = totalReceived;
        let totalCommitted = 0;
        let totalShipped = 0;
        let totalInventoryItems = 0;
        let totalInvItemOnHand = 0;
        let totalInvItemCommitted = 0;
        let hasErrors = false;

        if (inventory.otherException !== true) {
          if (inventory.shipmentLineItems?.items?.length > 0) {
            for (const lineItem of inventory.shipmentLineItems.items) {
              const qtyToPick = lineItem.totalQuantityToPick;
              if (lineItem.status === ShipmentLineItemStatus.SHIPPED) {
                remainingOnHand = trimDecimal(remainingOnHand - qtyToPick);
                totalShipped = trimDecimal(totalShipped + qtyToPick);
              } else totalCommitted = trimDecimal(totalCommitted + qtyToPick);
            }
          }

          if (inventory.inventoryItems?.items?.length > 0) {
            totalInventoryItems = inventory.inventoryItems?.items?.length;
            for (const invItem of inventory.inventoryItems.items) {
              totalInvItemOnHand = trimDecimal(
                totalInvItemOnHand + invItem.quantityOnHand,
              );
              totalInvItemCommitted = trimDecimal(
                totalInvItemCommitted + invItem.quantityCommitted,
              );
            }
          }

          // check quantityReceived
          if (
            inventory.status === InventoryStatus.UNLOADED &&
            inventory.quantityReceived !== totalInvItemOnHand
          ) {
            console.log(
              "quantity Received !== totalInvItemOnHand",
              inventory.id,
              inventory.quantityReceived,
              totalInvItemOnHand,
            );
            inventory.quantityReceived = totalInvItemOnHand;
            hasErrors = true;
          }

          // Check over/under shipment
          const actualReceived =
            inventory.quantityReceived +
            (inventory.overShipmentQuantity || 0) -
            (inventory.underShipmentQuantity || 0);
          if (
            inventory.status !== InventoryStatus.IN_TRANSIT &&
            inventory.status !== InventoryStatus.ARRIVED &&
            (inventory.quantityAdvised || 0) !== actualReceived
          ) {
            console.log(
              "quantityReceived !== quantityAdvised + Over/Under",
              inventory.id,
              inventory.quantityReceived,
              remainingOnHand,
              inventory.overShipmentQuantity,
              inventory.underShipmentQuantity,
            );
            // TODO
            // Decide on an appropriate fix - wait for prod issue to understand potential causes
          }

          // check quantityOnHand
          if (
            inventory.status !== InventoryStatus.IN_TRANSIT &&
            inventory.status !== InventoryStatus.ARRIVED &&
            inventory.status !== InventoryStatus.UNLOADED &&
            !(
              isTrack(inventory.area) &&
              inventory.inboundShipment.status === InboundStatus.RELEASED
            ) // can release bulk with qtyOnHand > 0
          ) {
            if (
              inventory.quantityOnHand !== remainingOnHand ||
              inventory.quantityOnHand !== totalInvItemOnHand
            ) {
              console.log(
                "quantityOnHand !== remainingOnHand !== totalInvItemOnHand",
                inventory.id,
                inventory.quantityOnHand,
                remainingOnHand,
                totalInvItemOnHand,
              );
              if (remainingOnHand !== totalInvItemOnHand) {
                console.log(
                  "    remainingOnHand !== totalInvItemOnHand",
                  remainingOnHand,
                  totalInvItemOnHand,
                );
                // remainingOnHand = totalInvItemOnHand;
              } else {
                inventory.quantityOnHand = remainingOnHand;
                hasErrors = true;
              }
            }
          }

          if (
            inventory.quantityCommitted !== totalCommitted ||
            inventory.quantityCommitted !== totalInvItemCommitted
          ) {
            console.log(
              "quantityCommitted !== totalCommitted",
              inventory.id,
              inventory.quantityCommitted,
              totalCommitted,
            );
            if (totalCommitted !== totalInvItemCommitted) {
              console.log(
                "    quantityCommitted !== totalInvItemCommitted",
                inventory.quantityCommitted,
                totalInvItemCommitted,
              );
              // totalCommitted = totalInvItemCommitted;
            } else {
              inventory.quantityCommitted = totalCommitted;
              hasErrors = true;
            }
          }
        }

        // checks STATUS after qty On Hand and Committed are corrected.
        if (
          inventory.status !== InventoryStatus.IN_TRANSIT &&
          inventory.status !== InventoryStatus.ARRIVED &&
          inventory.status !== InventoryStatus.ARCHIVED &&
          inventory.status !== InventoryStatus.SHIPPED
        ) {
          // status === STORED || UNLOADED
          if (
            totalShipped > 0 &&
            totalReceived > 0 &&
            inventory.quantityOnHand === 0
          ) {
            console.log(
              "quantityOnHand = 0 status !== SHIPPED",
              inventory.id,
              totalReceived,
              inventory.quantityOnHand,
              totalShipped,
              inventory.status,
            );
            inventory.quantityOnHand = 0;
            inventory.status = "SHIPPED";
            hasErrors = true;
          }
        } else if (
          inventory.quantityOnHand > 0 &&
          inventory.status === InventoryStatus.SHIPPED &&
          !(
            isTrack(inventory.area) &&
            inventory.inboundShipment.status === InboundStatus.RELEASED
          ) // can release bulk with qtyOnHand > 0
        ) {
          console.log(
            "quantityOnHand > 0 status === SHIPPED",
            inventory.id,
            inventory.quantityOnHand,
            totalReceived,
            inventory.status,
            inventory.inboundShipment.status,
          );
          inventory.status =
            inventory.inboundShipment.state === InboundState.COMPLETED
              ? InventoryStatus.STORED
              : InventoryStatus.UNLOADED;
          hasErrors = true;
        } else if (
          (inventory.quantityOnHand > 0 ||
            (totalShipped > 0 && totalShipped < totalReceived)) &&
          inventory.status !== InventoryStatus.STORED &&
          !(
            isTrack(inventory.area) &&
            inventory.inboundShipment.status === InboundStatus.RELEASED
          ) // can release bulk with qtyOnHand > 0
        ) {
          console.log(
            "quantityOnHand > 0 status !== STORED",
            inventory.id,
            inventory.quantityOnHand,
            totalShipped,
            inventory.status,
            inventory.inboundShipment.status,
          );
          inventory.status = InventoryStatus.STORED;
          hasErrors = true;
        }

        if (!inventory.inventoryUOM) {
          console.log("missing inventoryUOM", inventory.id);
          inventory.inventoryUOM = inventory.inboundShipment.totalUOM || "UNIT";
          hasErrors = true;
        }

        if (
          inventory.status !== InventoryStatus.IN_TRANSIT &&
          inventory.status !== InventoryStatus.ARRIVED &&
          totalInventoryItems === 0
        ) {
          console.log("missing inventoryItem:", inventory.id);
        }

        if (hasErrors && !reportOnly) {
          promiseList.push(saveInventory(inventory));
        }
      }

      debugger;
      const response = await Promise.allSettled(promiseList);
      response.forEach((resp) => {
        if (resp.status === "rejected") console.log("REJECTED: ", resp);
      });
    }
  } catch (err) {
    logError("AdminDashboard:fixInventoryQty", err, notify);
  }
  notify("All done");
};

// ################################################################
// ################################################################
// ################################################################
// ################################################################
// ################################################################
// ################################################################

export const deleteReloadYard = async (queryTerms, notify) => {
  console.clear();
  console.log("reload yard:", queryTerms.reloadYardId);

  try {
    // Simply pass the reloadYardId to deleteYardData
    await deleteYardData(queryTerms.reloadYardId, notify);
  } catch (err) {
    logError("AdminDashboard:deleteReloadYard", err, notify);
  }
};

export const resetTestData = async (queryTerms, notify) => {
  console.clear();
  console.log("reset test data for yard:", queryTerms.reloadYardId);

  try {
    await resetYardData(queryTerms.reloadYardId, notify);
  } catch (err) {
    logError("AdminDashboard:resetTestData", err, notify);
  }
};

export const resetCarrierData = async (queryTerms, notify) => {
  console.clear();
  console.log("reset carrier app data for yard:", queryTerms.reloadYardId);

  try {
    await resetCarrierAppData(queryTerms.reloadYardId, notify);
  } catch (err) {
    logError("AdminDashboard:resetCarrierData", err, notify);
  }
};

export const deleteAccountById = async (
  queryTerms,
  notify,
  deleteTrue = true,
) => {
  console.clear();
  console.log("account:", queryTerms.accountId);

  try {
    // Simply pass the reloadYardId to deleteYardData
    await deleteAccount(queryTerms.accountId, deleteTrue, notify);
  } catch (err) {
    logError("AdminDashboard:deleteAccount", err, notify);
  }
};

export const AccountChangeDialogContentAccountById = ({
  reloadYards,
  queryTerms,
  setQueryTerms,
}) => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TextInput
          id="accountId"
          label="Account ID"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          value={queryTerms.accountId}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, accountId: e.target.value })
          }
          style={{ minWidth: "32rem" }}
        />
      </Grid>
    </Grid>
  );
};

export const UpdateGroupNameDialogContent = ({
  reloadYards,
  queryTerms,
  setQueryTerms,
}) => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TextInput
          select
          id="yardId"
          label="Yard ID"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          value={queryTerms.reloadYardId}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, reloadYardId: e.target.value })
          }
          style={{ minWidth: "32rem" }}
        >
          {reloadYards.map((itm) => (
            <MenuItem key={itm.id} value={itm.id}>
              {itm.name} - {itm.id}
            </MenuItem>
          ))}
        </TextInput>
      </Grid>
      <Grid item xs={12}>
        <TextInput
          id="newGrpupName"
          label="New Group Name"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          value={queryTerms.newGrpupName || ""}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, newGrpupName: e.target.value })
          }
          style={{ minWidth: "32rem" }}
        />
      </Grid>
    </Grid>
  );
};

export const HandlerDialog = ({
  dialogOpen,
  handleDialogClose,
  onSubmit,
  handlerName,
  loading,
  reloadYards,
  queryTerms,
  setQueryTerms,
  orphanAudit,
}) => {
  const classes = useStyles();

  return (
    <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg">
      <DialogTitle id="alert-dialog-title">
        <Typography variant="h5" component="div">
          {handlerName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Backdrop className={classes.backdrop} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        {handlerName === "runDBIndexRegen" ? (
          <IndexRegenDialogContent
            queryTerms={queryTerms}
            setQueryTerms={setQueryTerms}
          />
        ) : handlerName === "runAccountChange" ? (
          <AccountChangeDialogContent
            reloadYards={reloadYards}
            queryTerms={queryTerms}
            setQueryTerms={setQueryTerms}
          />
        ) : handlerName === "deleteAccountById" ? (
          <AccountChangeDialogContentAccountById
            reloadYards={reloadYards}
            queryTerms={queryTerms}
            setQueryTerms={setQueryTerms}
          />
        ) : handlerName === "updateGroupName" ? (
          <UpdateGroupNameDialogContent
            reloadYards={reloadYards}
            queryTerms={queryTerms}
            setQueryTerms={setQueryTerms}
          />
        ) : handlerName === "deleteBulkErrorLogs" ? (
          <DeleteBulkErrorLogsDialogContent
            queryTerms={queryTerms}
            setQueryTerms={setQueryTerms}
          />
        ) : handlerName === "cleanupOrphanInventoryItems" ? (
          <OrphanCleanupDialogContent
            orphanAudit={orphanAudit}
            queryTerms={queryTerms}
            setQueryTerms={setQueryTerms}
          />
        ) : handlerName === "backfillOutboundPlacementDateTime" ? (
          <BackfillPlacementDialogContent
            reloadYards={reloadYards}
            queryTerms={queryTerms}
            setQueryTerms={setQueryTerms}
          />
        ) : handlerName === "backfillTrackCar" ||
          handlerName === "backfillTrackCarAisleZoneArea" ||
          handlerName === "backfillTrackCarEod" ||
          handlerName === "backfillTrackCarActive" ? (
          <BackfillTrackCarDialogContent
            queryTerms={queryTerms}
            setQueryTerms={setQueryTerms}
          />
        ) : handlerName === "resetGateCheckInData" ? (
          // Environment-wide, not per yard: drivers and their biometrics are
          // global records, so there is nothing to pick here.
          <>
            <Typography variant="body1" gutterBottom>
              This resets the gate check-in flow across the whole environment,
              not one yard.
            </Typography>
            <Typography variant="body1" gutterBottom>
              It deletes every gate check-in and driver session, clears the
              check-in lock those held on their shipments, and deletes every
              driver account with its carrier memberships.
            </Typography>
            <Typography variant="body1" gutterBottom>
              It also deletes the drivers' biometrics: enrollment records, face
              templates in Rekognition, and the face and licence images in S3.
            </Typography>
            <Typography variant="body1" gutterBottom>
              Carriers, dispatch users and shipments are left alone. Drivers
              re-enroll at the gate afterwards.
            </Typography>
          </>
        ) : handlerName === "backfillGateQueue" ? (
          // Environment-wide: the rename touched every yard's gate rows.
          <>
            <Typography variant="body1" gutterBottom>
              This rewrites every gate check-in row written before the status
              rename, across the whole environment.
            </Typography>
            <Typography variant="body1" gutterBottom>
              CHECKED_IN becomes ARRIVED, AT_DOCK becomes DOCKED, the check-in
              time moves to the arrived time, and the queue index key is
              recomposed so the rows show on the Gate Queue again.
            </Typography>
            <Typography variant="body1" gutterBottom>
              Rows already renamed are skipped, so this is safe to run more than
              once. Run it after the backend push that recreated the gate
              indexes.
            </Typography>
          </>
        ) : handlerName === "resetCarrierData" ? (
          <>
            <Typography variant="body1" gutterBottom>
              This deletes the carriers of the selected reload yard, their
              memberships, and the carrier users left without a yard.
            </Typography>
            <Typography variant="body1" gutterBottom>
              Shipments and timeslots are kept, but lose their carrier and
              driver: the loads disappear from every carrier's list.
            </Typography>
            <Typography variant="body1" gutterBottom>
              Drivers, gate check-ins and biometrics go with it — and that part
              is environment-wide, not limited to the yard picked below.
            </Typography>
            <Typography variant="body1" gutterBottom>
              Archived carriers and users are deleted too. "All Yards"
              additionally empties the three carrier tables of rows no live yard
              can reach — carriers of deleted yards, and users who never
              completed registration; picking one yard only counts those in the
              console.
            </Typography>
            <Typography variant="body1" gutterBottom>
              "All Yards" also deletes the CarrierCompany records above the
              yards, which releases the regulatory numbers they had claimed
              platform-wide — until that happens, re-registering a carrier under
              the same USDOT number is refused. The release runs on a stream and
              lands a moment after the reset finishes; the console reports what
              was still standing.
            </Typography>
            <TextInput
              select
              id="yardId"
              label="Yard ID"
              variant="outlined"
              margin="dense"
              fullWidth
              required
              value={queryTerms.reloadYardId}
              onChange={(e) =>
                setQueryTerms({ ...queryTerms, reloadYardId: e.target.value })
              }
              style={{ minWidth: "32rem" }}
            >
              <MenuItem value={ALL_RELOAD_YARDS}>
                <strong>All Yards</strong>
              </MenuItem>
              {reloadYards.map((itm) => (
                <MenuItem key={itm.id} value={itm.id}>
                  {" "}
                  {itm.name} - {itm.id}{" "}
                </MenuItem>
              ))}
            </TextInput>
          </>
        ) : handlerName === "resetTestData" ? (
          <>
            <Typography variant="body1" gutterBottom>
              This only resets the selected reload yard.
            </Typography>
            <Typography variant="body1" gutterBottom>
              It deletes invoices, invoice services, and other test data for
              that yard.
            </Typography>
            <Typography variant="body1" gutterBottom>
              It preserves Location and Aisle rows but clears their runtime
              fields.
            </Typography>
            <Typography variant="body1" gutterBottom>
              The yard's gate check-ins and driver sessions go with the
              shipments they name, so the Gate Queue comes back empty. The
              drivers themselves and their biometrics are left alone — those are
              environment-wide and belong to the two buttons above.
            </Typography>
            <TextInput
              select
              id="yardId"
              label="Yard ID"
              variant="outlined"
              margin="dense"
              fullWidth
              required
              value={queryTerms.reloadYardId}
              onChange={(e) =>
                setQueryTerms({ ...queryTerms, reloadYardId: e.target.value })
              }
              style={{ minWidth: "32rem" }}
            >
              {reloadYards.map((itm) => (
                <MenuItem key={itm.id} value={itm.id}>
                  {" "}
                  {itm.name} - {itm.id}{" "}
                </MenuItem>
              ))}
            </TextInput>
          </>
        ) : (
          <TextInput
            select
            id="yardId"
            label="Yard ID"
            variant="outlined"
            margin="dense"
            fullWidth
            required
            value={queryTerms.reloadYardId}
            onChange={(e) =>
              setQueryTerms({ ...queryTerms, reloadYardId: e.target.value })
            }
            style={{ minWidth: "32rem" }}
          >
            {reloadYards.map((itm) => (
              <MenuItem key={itm.id} value={itm.id}>
                {" "}
                {itm.name} - {itm.id}{" "}
              </MenuItem>
            ))}
          </TextInput>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>Cancel</Button>
        <Button color="primary" onClick={() => onSubmit(queryTerms)}>
          Execute
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const IndexRegenDialogContent = ({ queryTerms, setQueryTerms }) => {
  const selectedResource =
    resourceOptions.find((itm) => itm.id === queryTerms.resource) || null;

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Autocomplete
          options={resourceOptions}
          getOptionLabel={(option) => option?.name || ""}
          value={selectedResource}
          onChange={(_, option) =>
            setQueryTerms({ ...queryTerms, resource: option?.id || "" })
          }
          renderInput={(params) => (
            <TextInput
              {...params}
              id="table"
              label="Table Name"
              variant="outlined"
              margin="dense"
              fullWidth
              required
              style={{ minWidth: "32rem" }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export const AccountChangeDialogContent = ({
  reloadYards,
  queryTerms,
  setQueryTerms,
}) => {
  const [accounts, setAccounts] = useState([]);
  const [results, setResults] = useState([]);

  const handleYandChange = async (reloadYardId) => {
    setQueryTerms({ ...queryTerms, reloadYardId: reloadYardId });

    let response = await API.graphql(
      graphqlOperation(queries.accountByActiveByName, {
        reloadYardId: reloadYardId,
      }),
    );
    const accounts = response.data.accountByActiveByName.items;
    debugger;
    setAccounts(accounts);
  };

  const handleInboundSearch = async () => {
    let searchInput = {
      filter: {
        reloadYardId: { eq: queryTerms.reloadYardId },
        accountId: { eq: queryTerms.fromAccount.id },
      },
      limit: 10,
    };
    if (queryTerms.searchTerm) {
      const value = queryTerms.searchTerm;
      searchInput.filter.or = [
        { id: { eq: `${value}` } },
        { poNumber: { matchPhrasePrefix: `${value}` } },
        { poNumber: { wildcard: `*${value}*` } },
        { railCarNumber: { matchPhrasePrefix: `${value}` } },
        { railCarNumber: { wildcard: `*${value}*` } },
        { lotNumber: { matchPhrasePrefix: `${value}` } },
        { lotNumber: { wildcard: `*${value}*` } },
        { customFields: { matchPhrasePrefix: `${value}` } },
        { customFields: { wildcard: `*${value}*` } },
      ];
    }
    let response = await API.graphql(
      graphqlOperation(adminQueries.searchInboundShipments, searchInput),
    );
    const inbounds = response.data.searchInboundShipments;

    debugger;
    inbounds.items = inbounds.items.map((inbound) => {
      let hasInvoiceItems = inbound?.invoice?.lineItems?.items?.length > 0;
      let hasProducts = false;
      if (inbound.inventories && inbound.inventories.items) {
        for (let item of inbound.inventories.items) {
          if (item.productId) {
            hasProducts = true;
            break;
          }
        }
      }
      return { ...inbound, hasInvoiceItems, hasProducts };
    });

    setResults(inbounds);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5">
          DO NOT RUN IF INBOUNDS / OUTBOUNDS HAVE BEEN INVOICED WITH SERVICE
          ITEMS
        </Typography>
        <Typography variant="h5">INVENTORY PRODUCT ID WILL RESET</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextInput
          select
          id="yardId"
          label="Yard ID"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          value={queryTerms.reloadYardId}
          onChange={(e) => handleYandChange(e.target.value)}
          style={{ minWidth: "32rem" }}
        >
          {reloadYards.map((itm) => (
            <MenuItem key={itm.id} value={itm.id}>
              {" "}
              {itm.name} - {itm.id}{" "}
            </MenuItem>
          ))}
        </TextInput>
      </Grid>
      <Grid item xs={12}>
        <TextInput
          select
          id="fromAccount"
          label="From Account"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          value={queryTerms.fromAccount}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, fromAccount: e.target.value })
          }
          style={{ minWidth: "32rem" }}
        >
          {accounts.map((itm) => (
            <MenuItem key={itm.id} value={itm}>
              {" "}
              {itm.businessName} - {itm.id}{" "}
            </MenuItem>
          ))}
        </TextInput>
      </Grid>
      <Grid item xs={12}>
        <TextInput
          id="searchTerm"
          label="Search Term"
          variant="outlined"
          margin="dense"
          fullWidth
          value={queryTerms.searchTerm}
          onChange={(e) =>
            setQueryTerms({ ...queryTerms, searchTerm: e.target.value })
          }
          style={{ minWidth: "32rem" }}
          helperText="inbound ID, poNumber, railCarNumber, lotNumber, customFields"
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          disabled={!queryTerms.reloadYardId || !queryTerms.fromAccount?.id}
          onClick={handleInboundSearch}
        >
          Search
        </Button>
      </Grid>
      {results?.items?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h5">SAMPLE 10 ROWS</Typography>
          <ArrayField source="items" record={results}>
            <Datagrid>
              <TextField source="id" />
              <TextField source="poNumber" />
              <TextField source="railCarNumber" />
              <TextField source="lotNumber" />
              <BooleanField source="hasProducts" />
              <BooleanField source="hasInvoiceItems" />
            </Datagrid>
          </ArrayField>
        </Grid>
      )}
      <Grid item xs={12}>
        <SwitchOnOff
          leftLabel="Refresh Account Name"
          rightLabel="Change Account ID"
          checked={queryTerms.changeId}
          // onChange={() => setQueryTerms({ ...queryTerms, changeId: !queryTerms.changeId })}
          setChecked={() =>
            setQueryTerms({ ...queryTerms, changeId: !queryTerms.changeId })
          }
        />
        {/*
            if change-id == TRUE, change both accoundId and accountName
            if change-id == FALSE, only refresh accountName
          */}
      </Grid>
      {queryTerms?.changeId && (
        <Grid item xs={12}>
          <TextInput
            select
            id="toAccount"
            label="To Account"
            variant="outlined"
            margin="dense"
            fullWidth
            required
            value={queryTerms.toAccount}
            onChange={(e) =>
              setQueryTerms({ ...queryTerms, toAccount: e.target.value })
            }
            style={{ minWidth: "32rem" }}
          >
            {accounts.map((itm) => (
              <MenuItem key={itm.id} value={itm}>
                {" "}
                {itm.businessName} - {itm.id}{" "}
              </MenuItem>
            ))}
          </TextInput>
        </Grid>
      )}
    </Grid>
  );
};

const runSettled = async (promises) => {
  const results = await Promise.allSettled(promises);
  const failures = results.filter((r) => r.status === "rejected");
  return { ok: results.length - failures.length, failed: failures.length };
};

export const backFillUnFilledContainerDropValuesInbounds = async (notify) => {
  console.clear();
  notify("Starting back-fill: inbound.containerDrop ⇒ false where empty…");

  const searchInput = { limit: 1000 };
  let nextToken = true,
    updated = 0;

  try {
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { ...searchInput, nextToken };
      const { data } = await API.graphql(
        graphqlOperation(adminQueries.listInboundShipmentsContainerDrop, input),
      );

      const inbounds = data?.listInboundShipments?.items ?? [];
      nextToken = data?.listInboundShipments?.nextToken ?? null;

      if (!inbounds.length) continue;

      const batch = inbounds
        .filter((b) => b.containerDrop == null)
        .map((b) =>
          API.graphql(
            graphqlOperation(mutations.updateInboundShipment, {
              input: { id: b.id, containerDrop: false },
            }),
          ),
        );

      const { ok } = await runSettled(batch);
      updated += ok;
    }

    notify(`All done — updated ${updated} inbound record(s).`);
  } catch (err) {
    logError("AdminDashboard:backFillContainerDrop", err, notify);
  }
};

export const backFillUnFilledContainerPickupValuesOutbounds = async (
  notify,
) => {
  console.clear();
  notify("Starting back-fill: outbound.containerPickup ⇒ false where empty…");

  const searchInput = { limit: 1000 };
  let nextToken = true,
    updated = 0;

  try {
    while (nextToken) {
      const input =
        nextToken === true ? searchInput : { ...searchInput, nextToken };
      const { data } = await API.graphql(
        graphqlOperation(
          adminQueries.listOutboundShipmentsContainerDrop,
          input,
        ),
      );

      const outbounds = data?.listOutboundShipments?.items ?? [];
      nextToken = data?.listOutboundShipments?.nextToken ?? null;

      if (!outbounds.length) continue;

      const batch = outbounds
        .filter((o) => o.containerPickup == null)
        .map((o) =>
          API.graphql(
            graphqlOperation(mutations.updateOutboundShipment, {
              input: { id: o.id, containerPickup: false },
            }),
          ),
        );

      const { ok } = await runSettled(batch);
      updated += ok;
    }

    notify(`All done — updated ${updated} outbound record(s).`);
  } catch (err) {
    logError("AdminDashboard:backFillContainerPickup", err, notify);
  }
};
